/**
 * AI OCR設定ページ
 *
 * システム全体のOCRベースプロンプトと発注元特定プロンプトを管理する。
 * 全管理者がアクセス・編集可能。
 */

import { useEffect, useState, useCallback } from "react";
import {
  Container,
  Title,
  Stack,
  Paper,
  Textarea,
  Button,
  Group,
  Alert,
  LoadingOverlay,
  Text,
  Tabs,
  Tooltip,
} from "@mantine/core";
import { Brain, Save, RotateCcw, HelpCircle, FileText, Search } from "lucide-react";
import {
  fetchSystemOcrPrompt,
  saveSystemOcrPrompt,
  type OcrPromptConfig,
} from "../services/systemConfigService";
import { showSuccess, showError } from "../utils/notifications";


export function AiOcrSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<OcrPromptConfig>({
    basePrompt: "",
    senderIdentificationPrompt: "",
  });
  const [defaults, setDefaults] = useState<OcrPromptConfig>({
    basePrompt: "",
    senderIdentificationPrompt: "",
  });
  /** 変更検知用の初期値 */
  const [originalConfig, setOriginalConfig] = useState<OcrPromptConfig>({
    basePrompt: "",
    senderIdentificationPrompt: "",
  });

  /** 設定を読み込み */
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSystemOcrPrompt();
      setConfig(data.config);
      setDefaults(data.defaults);
      setOriginalConfig(data.config);
    } catch (error) {
      console.error("OCRプロンプト設定の取得に失敗:", error);
      showError("設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /** 保存処理 */
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSystemOcrPrompt(config);
      setOriginalConfig(config);
      showSuccess("OCRプロンプト設定を保存しました");
    } catch (error) {
      console.error("OCRプロンプト設定の保存に失敗:", error);
      showError("設定の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  /** デフォルトに戻す */
  const handleResetToDefaults = () => {
    if (!confirm("プロンプトをデフォルト値に戻しますか？未保存の変更は失われます。")) return;
    setConfig(defaults);
  };

  /** 変更があるか */
  const hasChanges =
    config.basePrompt !== originalConfig.basePrompt ||
    config.senderIdentificationPrompt !== originalConfig.senderIdentificationPrompt;

  return (
    <Container size="lg">
      <Stack gap="lg" pos="relative">
        <LoadingOverlay visible={loading} />

        {/* ヘッダー */}
        <Group justify="space-between" align="center">
          <Group>
            <Brain size={28} />
            <Title order={2}>AI OCR設定</Title>
          </Group>
          <Group>
            <Tooltip label="デフォルトに戻す">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<RotateCcw size={16} />}
                onClick={handleResetToDefaults}
                disabled={loading}
              >
                デフォルトに戻す
              </Button>
            </Tooltip>
            <Button
              leftSection={<Save size={16} />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges || loading}
            >
              保存
            </Button>
          </Group>
        </Group>

        {/* 説明 */}
        <Alert
          icon={<HelpCircle size={16} />}
          title="AI OCRプロンプト設定について"
          color="blue"
          variant="light"
          radius="md"
        >
          <Text size="sm">
            AIがFAX伝票を読み取る際に使用するシステムプロンプトを設定します。
            プロンプトの内容がOCR精度に直接影響するため、変更後はPlaygroundで動作確認を行うことをお勧めします。
          </Text>
        </Alert>

        {/* タブ切り替え */}
        <Tabs defaultValue="base">
          <Tabs.List>
            <Tabs.Tab value="base" leftSection={<FileText size={16} />}>
              メインプロンプト
            </Tabs.Tab>
            <Tabs.Tab value="sender" leftSection={<Search size={16} />}>
              発注元特定プロンプト
            </Tabs.Tab>
          </Tabs.List>

          {/* メインプロンプト */}
          <Tabs.Panel value="base" pt="md">
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  OCR解析用のベースプロンプト
                </Text>
                <Text size="xs" c="dimmed">
                  FAX伝票画像を読み取り、構造化JSONとして出力する際のベースとなるプロンプトです。
                  取引先ごとの追加プロンプト（promptAddition）はこれに追記されます。
                </Text>
                <Textarea
                  value={config.basePrompt}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      basePrompt: e.target.value,
                    }))
                  }
                  autosize
                  minRows={20}
                  maxRows={40}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: "1.6",
                    },
                  }}
                />
                <Text size="xs" c="dimmed" ta="right">
                  {config.basePrompt.length} 文字
                </Text>
              </Stack>
            </Paper>
          </Tabs.Panel>

          {/* 発注元特定プロンプト */}
          <Tabs.Panel value="sender" pt="md">
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  発注元特定用プロンプト（2パスOCR 第1パス）
                </Text>
                <Text size="xs" c="dimmed">
                  2パスOCRの第1パスで使用する軽量プロンプトです。
                  伝票画像から発注元の会社名のみを特定し、取引先ごとのカスタム設定を適用するために使用されます。
                </Text>
                <Textarea
                  value={config.senderIdentificationPrompt}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      senderIdentificationPrompt: e.target.value,
                    }))
                  }
                  autosize
                  minRows={12}
                  maxRows={30}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: "1.6",
                    },
                  }}
                />
                <Text size="xs" c="dimmed" ta="right">
                  {config.senderIdentificationPrompt.length} 文字
                </Text>
              </Stack>
            </Paper>
          </Tabs.Panel>
        </Tabs>

      </Stack>
    </Container>
  );
}
